import React from 'react';

import type * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../../state/views/runSet/types';
import {observeMetricSelectionPainWindow} from '../../../util/datadog/criticalWorkflows/painWindow/metricSelection';
import {type MetricsPickerConfig} from '../../elements/MetricsPickerShared';
import {MetricSearchDropdown} from '../../MetricSearch/MetricSearchDropdown';
import {YAxisMetricPicker} from '../../MetricSearch/YAxisMetricPicker';
import {X_AXIS_LABELS} from '../defaults';
import {type RunsLinePlotConfig} from '../types';
import {
  type AxisScaffolding,
  AxisSections,
  DROPDOWN_FLEX_GROW_STYLE,
  X_AXIS_DROPDOWN_RESULT_LIMIT,
  type XAxisChangeHandler,
} from './AxisSections';

// Reserved x-axis keys prepended client-side to the metric-search results.
const RESERVED_X_AXIS_OPTIONS = [
  '_step',
  '_absolute_runtime',
  '_runtime',
  '_timestamp',
].map(key => ({key, value: key, text: X_AXIS_LABELS[key] ?? key}));

const X_AXIS_METRIC_TYPES = ['number'];

type MetricSearchAxisSectionsProps = AxisScaffolding & {
  runSetRefs: RunSetTypes.Ref[];
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined;
  config: MetricsPickerConfig;
  xAxis: string;
  onXAxisChange: XAxisChangeHandler;
  updateConfig: (config: RunsLinePlotConfig) => void;
};

export const MetricSearchAxisSections = ({
  xAxisRange,
  xAxisTitle,
  yAxisRange,
  yAxisTitle,
  onResetXAxis,
  runSetRefs,
  customRunNamesRef,
  config,
  xAxis,
  onXAxisChange,
  updateConfig,
}: MetricSearchAxisSectionsProps) => {
  const handleXAxisChange = React.useCallback<XAxisChangeHandler>(
    (event, data) => {
      if (typeof data.value === 'string') {
        observeMetricSelectionPainWindow({
          axis: 'x',
          event,
          initialSelection: [xAxis],
          nextSelection: [data.value],
          pickerVariant: 'metric_search',
        });
      }
      onXAxisChange(event, data);
    },
    [onXAxisChange, xAxis]
  );

  return (
    <AxisSections
      xAxisRange={xAxisRange}
      xAxisTitle={xAxisTitle}
      yAxisRange={yAxisRange}
      yAxisTitle={yAxisTitle}
      onResetXAxis={onResetXAxis}
      // This is the NGW metric-search variant, so AxisSections pins the label +
      // reset to the control line (the dropdown can render an error/retry row).
      usesMetricSearchPicker
      xPicker={
        <MetricSearchDropdown
          perfAction="edit-panel-x-metric-picker"
          types={X_AXIS_METRIC_TYPES}
          reservedOptions={RESERVED_X_AXIS_OPTIONS}
          value={xAxis}
          onChange={handleXAxisChange}
          placeholder="X Axis"
          resultLimit={X_AXIS_DROPDOWN_RESULT_LIMIT}
          style={DROPDOWN_FLEX_GROW_STYLE}
        />
      }
      yPicker={
        // Note: regex mode isn't handled yet
        <YAxisMetricPicker
          runSetRefs={runSetRefs}
          customRunNamesRef={customRunNamesRef}
          config={config}
          updateConfig={updateConfig}
        />
      }
    />
  );
};
